import { FileDropzone, InfoCard, palette } from '@innovyze/stylovyze';
import { useGlobalization } from '@Translations';
import React from 'react';
import {
	DropEvent,
	DropzoneOptions,
	FileError,
	FileRejection,
} from 'react-dropzone';
import { Container } from './BulkSensorUploader.styles';
import {
	StyledLi,
	StyledUl,
	ValidationInfoTitle,
} from '@Components/SensorDataUploader/SensorDataUploader.styles';
import { Grid } from '@mui/material';
import { decodeToUtf8 } from '@Utils';

interface FileWithCsv extends File {
	csv: string[][];
	error: string;
}

interface Props {
	statusText: string;
	onReadyForUpload: (files: File[]) => void;
}

export default function BulkSensorUploader(props: Props) {
	const { t } = useGlobalization();
	const [validationErrors, setValidationErrors] = React.useState<
		FileRejection[]
	>([]);

	const getFilesFromEvent = async (event: DropEvent) => {
		const files: File[] = Array.from(
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			//@ts-ignore
			event.target.files || event.dataTransfer.files,
		);

		const fileRes = Promise.all(
			files.map(async file => {
				try {
					const fileBuffer = await file.arrayBuffer();
					decodeToUtf8(fileBuffer);
					console.log('--- decoded ---');
					Object.defineProperty(file, 'csv', { value: file });
					return file;
				} catch (e) {
					Object.defineProperty(file, 'error', {
						value: 'error-decoding-uft-8',
					});
					return file;
				}
			}),
		);

		return fileRes;
	};

	const onDrop = (
		_acceptedFiles: File[],
		fileRejections: FileRejection[],
	) => {
		setValidationErrors(fileRejections);
	};

	const encodingNotSupportedError: FileError = {
		message: t('file encoding type not supported, please use utf-8'),
		code: 'sensor-encoding-not-supported',
	};

	const validator = (file: File): FileError | FileError[] | null => {
		const decodeError = (file as FileWithCsv).error;

		const fileErrors: FileError[] = [];
		if (decodeError) {
			fileErrors.push(encodingNotSupportedError);
		}

		return fileErrors.length ? fileErrors : null;
	};

	const onDropAccepted = async function (files: File[]) {
		props.onReadyForUpload(files);
	};

	const dropzoneOptions: DropzoneOptions = {
		onDrop,
		onDropAccepted,
		getFilesFromEvent,
		validator,
	};

	const infoCardTitle = `${t('File Attachments')}${
		props.statusText && ` - ${props.statusText}`
	}`;

	const fileValidationErrorInfo = validationErrors.map(validationErr => (
		<StyledLi
			color={palette.error.dark}
			key={validationErr.file.webkitRelativePath}>
			{`${validationErr.file.name} ${validationErr.errors.map(
				err => err.message,
			)}`}
		</StyledLi>
	));

	return (
		<Container id="create-bulk-sensor-uploader">
			<Container>
				<InfoCard fullHeight title={infoCardTitle}>
					<FileDropzone
						supportedFileText={t('Supported file type: csv')}
						// eslint-disable-next-line @typescript-eslint/no-explicit-any
						dropzoneOptions={dropzoneOptions as any}
						token={''}
					/>
				</InfoCard>
				{validationErrors.length > 0 && (
					<Grid item xs={12}>
						<Grid item xs={12}>
							<ValidationInfoTitle color={palette.error.dark}>
								{t('Validation Errors')}
							</ValidationInfoTitle>
						</Grid>
						<Grid item xs={12}>
							<StyledUl>{fileValidationErrorInfo}</StyledUl>
						</Grid>
					</Grid>
				)}
			</Container>
		</Container>
	);
}
